import {Period} from "@/edinet/types"
import {SetDocType, SetNews} from "@/set/types"

function period(p: string): Period {
    return Period[p.trim() as keyof typeof Period]
}

function year(y: string): number {
    return parseInt(y.trim())
}

function thaiYear(y: string): number {
    return year(y) - 543
}

export interface KnownDoc {
    setDocType: SetDocType
    period: Period
    year: number
}

interface KnownDocPattern {
    match(title: string, news: SetNews): (KnownDoc | undefined)
}

interface SimplePatternConfig {
    re: RegExp
    docType: SetDocType
    periodFn: (m: RegExpMatchArray) => Period
    yearFn: (m: RegExpMatchArray, news: SetNews) => number
}

class SimplePattern implements KnownDocPattern {
    config: SimplePatternConfig

    constructor(config: SimplePatternConfig) {
        this.config = config
    }

    match(title: string, news: SetNews): KnownDoc | undefined {
        const m = title.match(this.config.re)
        if (m) {
            try {
                return {
                    setDocType: this.config.docType,
                    period: this.config.periodFn(m),
                    year: this.config.yearFn(m, news)
                }
            } catch (e) {
                console.warn('error in simple pattern %o, %o', m, e)
            }
        }
    }

}

const matchers = [] as KnownDocPattern[]

const configs: SimplePatternConfig[] = [
    //Summary
    {
        docType: SetDocType.Summary,
        re: /Financial Performance Quarter (\d+)/,
        periodFn: m => period('Q' + m[1]),
        yearFn: (m, news) => year(news.date.substr(0, 4)) //take YYYY out of YYYY-MM-DD
    }, {
        docType: SetDocType.Summary,
        re: /Yearly financial performance/,
        periodFn: () => Period.FY,
        //since we don't have the year in the title - so assume year is filing date - 1
        yearFn: (m, news) => year(news.date.substr(0, 4)) - 1
    },
    //FinancialStatement
    {
        docType: SetDocType.FinancialStatement,
        re: /Financial Statement Half Year\/(\d+)/,
        periodFn: () => Period.Q2,
        yearFn: m => year(m[1])
    }, {
        docType: SetDocType.FinancialStatement,
        re: /Financial Statement Quarter (\d)\/(\d+)/,
        periodFn: m => period('Q' + m[1]),
        yearFn: m => year(m[2])
    }, {
        docType: SetDocType.FinancialStatement,
        re: /Financial Statement Yearly (\d+)/,
        periodFn: () => Period.FY,
        yearFn: m => year(m[1])
    },
    //MD&A
    {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis Quarter (\d) Ending \d+ \w+ (\d+)/,
        periodFn: m => period('Q' + m[1]),
        yearFn: m => year(m[2])
    }, {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis Yearly Ending \d+ \w+ (\d+)/,
        periodFn: () => Period.FY,
        yearFn: m => year(m[1])
    },
    {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis Quarter (\d)\/(\d+)/,
        periodFn: m => period('Q' + m[1]),
        yearFn: m => year(m[2])
    }, {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis Yearly (\d+)/,
        periodFn: () => Period.FY,
        yearFn: m => year(m[1])
    }, {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis .*September.*(\d{4})$/,
        periodFn: () => Period.Q3,
        yearFn: m => year(m[1])
    }, {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis .*June.*(\d{4})$/,
        periodFn: () => Period.Q2,
        yearFn: m => year(m[1])
    }, {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis .*March.*(\d{4})$/,
        periodFn: () => Period.Q1,
        yearFn: m => year(m[1])
    }, {
        docType: SetDocType.MDA,
        re: /Management Discussion and Analysis .*December.*(\d{4})$/,
        periodFn: () => Period.FY,
        yearFn: m => year(m[1])
    }
]
matchers.push(...configs.map(x => new SimplePattern(x)))

const thaiConfigs: SimplePatternConfig[] = [{
    docType: SetDocType.Summary,
    re: /สรุปผล.* ไตรมาสที่ (\d)/,
    periodFn: function(e) {
        return period("Q" + e[1])
    },
    yearFn: function(e, t) {
        return year(t.date.substr(0, 4))
    }
}, {
    docType: SetDocType.Summary,
    re: /สรุปผล.*ประจำปี/,
    periodFn: function() {
        return Period.FY
    },
    yearFn: function(e, t) {
        return year(t.date.substr(0, 4)) - 1
    }
}, {
    docType: SetDocType.Summary,
    re: /.*สรุปผล.*(?:มี.ค.|มีนาคม).*(\d{4})/,
    periodFn: function() {
        return Period.Q1
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.Summary,
    re: /.*สรุปผล.*(?:มิ.ย.|มิถุนายน).*(\d{4})/,
    periodFn: function() {
        return Period.Q2
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.Summary,
    re: /.*สรุปผล.*(?:ก.ย.|กันยายน).*(\d{4})/,
    periodFn: function() {
        return Period.Q3
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.Summary,
    re: /.*สรุปผล.*(?:ธ.ค.|ธันวาคม).*(\d{4})/,
    periodFn: function() {
        return Period.FY
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.FinancialStatement,
    re: /งบการเงิน \s?งวดครึ่งปี\/(\d+)/,
    periodFn: function() {
        return Period.Q2
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.FinancialStatement,
    re: /งบการเงิน\s?ไตรมาส.* (\d)\/(\d+)/,
    periodFn: function(e) {
        if (e[1] === "4") {
            return Period.FY
        } else {
            return period("Q" + e[1])
        }
    },
    yearFn: function(e) {
        return thaiYear(e[2])
    }
}, {
    docType: SetDocType.FinancialStatement,
    re: /งบการเงิน.*(รายปี|ประจำปี)\s?(\d+)/,
    periodFn: function() {
        return Period.FY
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.MDA,
    re: /.*คำอธิบาย.*(?:มี.ค.|มีนาคม).*(\d{4})/,
    periodFn: function() {
        return Period.Q1
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.MDA,
    re: /.*คำอธิบาย.*(?:มิ.ย.|มิถุนายน).*(\d{4})/,
    periodFn: function() {
        return Period.Q2
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.MDA,
    re: /.*คำอธิบาย.*(?:ก.ย.|กันยายน).*(\d{4})/,
    periodFn: function() {
        return Period.Q3
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.MDA,
    re: /.*คำอธิบาย.*(?:ธ.ค.|ธันวาคม).*(\d{4})/,
    periodFn: function() {
        return Period.FY
    },
    yearFn: function(e) {
        return thaiYear(e[1])
    }
}, {
    docType: SetDocType.MDA,
    re: /.*คำอธิบาย.*(?:ไตรมาสที่).*(\d)\/(\d{4})/,
    periodFn: function(e) {
        const s = 'Q' + e[1]
        if (s === 'Q4') {
            return Period.FY
        } else {
            return period(s)
        }
    },
    yearFn: function(e) {
        return thaiYear(e[2])
    }
}]
matchers.push(...thaiConfigs.map(x => new SimplePattern(x)))

export function matchKnownDoc(title: string, news: SetNews): (KnownDoc | undefined) {
    for (let i = 0; i < matchers.length; i++) {
        const knownDoc = matchers[i].match(title, news)
        if (knownDoc) {
            return knownDoc
        }
    }
}